import { AppContext } from "@lightspeed-invoicing/core-client/frontend"
import React, { useEffect, useState } from "react"
import { Button } from "react-bootstrap"
import { FormattedMessage, injectIntl } from "react-intl"
import { useMutation, useQuery } from "react-query"
import { messages } from "../../../common/credit-card/rf-components/messages"
import PaymentAccount, { PaymentAccountDto } from "../../../payment-account/model/paymentaccount"
import Session from "../../../payment-account/model/session"
import SessionService from "../../../payment-account/service/sessionService"
import { useGetPaymentAccounts } from "../../../payment-processing/usePaymentAccountSession"
import { CHECKOUT_COOKIE_KEY, PRICING_TABLE_COOKIE_KEY, clearCookies } from "../../../price-list/helpers/utils"
import { APIError, HttpError, isError } from "../../../state/helpers/Error"
import IntlProps from "../../../state/helpers/IntlProps"
import { PAYMENT_SOURCE, PAYMENT_STATUS, PAYMENT_TYPE } from "../../../state/helpers/Payment"
import { INTEGRATION_KEY } from "../../../state/integration/Constants"
import LoggerService from "../../../state/logger/LoggerService"
import PaymentIntentService from "../../../state/payment-intent/PaymentIntentService"
import { Metadata } from "../../../state/payment-processor/metadata"
import PaymentProcessorService from "../../../state/payment-processor/paymentProcessorService"
import { PayinConfig } from "../../../state/payment-processor/rainforest/Payin"
import Payment from "../../../state/payment/Payment"
import { OrderFormPayload } from "../../../state/price-list/orderFormPayload"
import Proposal, { ProposalDto } from "../../../state/proposal/Proposal"
import ProposalService from "../../../state/proposal/proposalService"
import { SESSION_TYPE, VALID_DOCUMENT_TYPE } from "../../../utils"
import { CURRENCY } from "../../../utils/currency/constants"
import ProcessingPaymentLoader from "../../credit-card/rf-components/ProcessingPaymentLoader"
import RainforestErrorRefreshButton from "../../credit-card/rf-components/RainforestErrorRefreshButton"
import RainforestPayment from "../../credit-card/rf-components/RainforestPayment"
import { RF_METHOD_TYPES } from "../../credit-card/rf-components/constants"
import { WarningIcon } from "../../icon/icons/WarningIcon"
import LoadingSpinner from "../../spinner/loading.component"

type OwnProps = {
    amount: number
    buildMetaData?: () => Metadata
    onClose: () => void
    proposalHash: string
    paymentAccount?: PaymentAccountDto
    forceSaveBankAccount?: boolean
}

type PaymentResult = {
    isNewPaymentCreated: boolean
    status: string
    payment: Payment | undefined
}

const REFRESH_INTERVAL = 1000
const MAX_POLL_ATTEMPTS = 30

const RainforestBankAccountWrapper = (props: OwnProps & IntlProps) => {

    const isTemplate = window.location.pathname.startsWith("/templates/")

    const metaData = props.buildMetaData?.()

    const isSupportedCurrency = (): boolean => {
        if (metaData?.accountStatementId) {
            return metaData?.currency == CURRENCY.USD && metaData?.accountStatementInvoices.every(i => i.currency === CURRENCY.USD)
        }
        return metaData?.currency == CURRENCY.USD && metaData?.documentCurrency == CURRENCY.USD
    }

    const isProposal = (): boolean => {
        if (props.buildMetaData) {
            const metaData = props.buildMetaData()
            return metaData.source === PAYMENT_SOURCE.ONLINE
        }
        return false
    }

    let merchantID
    if (!isProposal() || isTemplate) {
        const { paymentAccounts } = useGetPaymentAccounts()
        if (props.buildMetaData) {
            const metaData = props.buildMetaData()
            const pa = paymentAccounts?.find(pa => (pa.getLocationIds().includes(metaData?.locationId)))
            merchantID = pa?.getMerchantId()
        }
    }
    if (props.paymentAccount) {
        const paymentAcc = new PaymentAccount(props.paymentAccount)
        merchantID = paymentAcc.getMerchantId()
    }

    const [isPolling, setIsPolling] = useState<boolean>(false)
    const [paymentFailed, setPaymentFailed] = useState<boolean>(false)
    const [previousPayment, setPreviousPayment] = useState<Payment | undefined>(undefined)
    const [saveBankAccountOnFile, setSaveBankAccountOnFile] = useState<boolean>(true)
    const [tentativeError, setTentativeError] = useState<string>("")
    //should ONLY be set for order forms
    const [finalProposalHash, setFinalProposalHash] = useState<string>("")

    const createPayinConfig = async () => {
        if (props.buildMetaData) {
            const metaData = props.buildMetaData()
            metaData.savePaymentMethodOnFile = saveBankAccountOnFile
            metaData.paymentMethod = PAYMENT_TYPE.BANK_TRANSFER
            metaData.ignoreWebhook = isPayingOrderForm()
            return await PaymentProcessorService.createPayinConfig(
                props.amount,
                merchantID,
                "",
                metaData,
                INTEGRATION_KEY.RAINFOREST,
            )
        }
    }

    const isPayingOrderForm = (): boolean => {
        return metaData?.documentType == VALID_DOCUMENT_TYPE.PRICE_LIST
    }

    const isPayingAccountStatementProposal = (): boolean => {
        return isProposal() && !!metaData?.accountStatementId
    }

    const isAmountValid = (): boolean => {
        return !!props.amount && props.amount > 0 && !isNaN(props.amount)
    }

    const { data: payinConfigData, isLoading: isLoadingData, isFetching: isFetching, refetch } = useQuery(
        ["createPayinConfig", props.amount], createPayinConfig,
        {
            enabled: !!merchantID && isAmountValid() && !!props.buildMetaData
        }
    )

    useEffect(() => {
        if (props.buildMetaData && payinConfigData && !isError(payinConfigData)) {
            PaymentIntentService.createPaymentIntent({
                externalId: payinConfigData.getId(),
                paymentProcessorKey: INTEGRATION_KEY.RAINFOREST,
                paymentMethodType: PAYMENT_TYPE.BANK_TRANSFER,
                savePaymentMethodOnFile: saveBankAccountOnFile,
                accountId: props.buildMetaData().accountId,
            })
        }
    }, [payinConfigData, saveBankAccountOnFile])

    useEffect(() => {
        const fetchData = async () => {
            const proposal = await fetchProposal(props.proposalHash)
            if (proposal) {
                setPreviousPayment(proposal.getMostRecentPayment())
            }
        }
        if (props.proposalHash) {
            fetchData()
        }

        const component = document.querySelector("rainforest-payment")
        return () => {
            if (component) {
                component.removeEventListener("approved", (event) => handleApprovedEvent(event, {}))
            }
        }
    }, [])

    useEffect(() => {
        if (isPayingOrderForm() && finalProposalHash) {
            pollForPaymentCreation()
        } else if (!props.proposalHash && metaData?.transactions && finalProposalHash) {
            pollForPaymentCreation()
        }
    }, [finalProposalHash])

    const { data: session, isLoading: isLoadingSession } = useQuery(
        ["createSession"],
        () => SessionService.getSession(SESSION_TYPE.CREATE_PAYIN, merchantID, isProposal()),
    )

    const { isLoading: isCreatingTransaction, isSuccess: isTransactionSuccess } = useMutation(
        ({ hash, payload }: { hash: string, payload: OrderFormPayload }) => {
            return ProposalService.createPaymentTransaction(hash, payload)
        })

    const pollForPaymentCreation = async () => {
        setIsPolling(true)

        let pollAttempt = 0
        const poll = setInterval(async () => {
            if (pollAttempt >= MAX_POLL_ATTEMPTS) {
                clearInterval(poll)
                setIsPolling(false)
                return
            }

            const { isNewPaymentCreated, status, payment } = await refreshDocument()
            if (isNewPaymentCreated && !!payment?.getBankAccountLast4()) {
                clearInterval(poll)
                setIsPolling(false)
                if (status == PAYMENT_STATUS.DECLINED) {
                    setPaymentFailed(true)
                } else {
                    if (isPayingOrderForm()) {
                        clearCookies(PRICING_TABLE_COOKIE_KEY)
                        clearCookies(CHECKOUT_COOKIE_KEY)
                        window.location.href = "/p/proposals/" + finalProposalHash
                    }
                    props.onClose()
                }
            }

            pollAttempt++
        }, REFRESH_INTERVAL)
    }

    const fetchProposal = async (hash: string): Promise<Proposal | undefined> => {
        const response = await ProposalService.getProposal(hash)
        return isError(response) ? undefined : response as Proposal
    }

    const refreshDocument = async (): Promise<PaymentResult> => {
        const proposal = await fetchProposal(finalProposalHash || props.proposalHash)
        if (proposal) {
            const newlyCreatedPayment = proposal.getMostRecentPayment()
            //handle case when no payments exist on document yet
            if (!newlyCreatedPayment) {
                return {
                    isNewPaymentCreated: false,
                    status: "",
                    payment: undefined,
                }
            }

            const isNewPaymentCreated = validateNewPaymentWasCreated(newlyCreatedPayment)
            if (isNewPaymentCreated) {
                setPreviousPayment(newlyCreatedPayment)
            }

            return {
                isNewPaymentCreated,
                status: newlyCreatedPayment.getStatus(),
                payment: newlyCreatedPayment,
            }
        }

        return {
            isNewPaymentCreated: false,
            status: "",
            payment: undefined,
        }
    }

    const validateNewPaymentWasCreated = (newlyCreatedPayment: Payment): boolean => {
        if (newlyCreatedPayment && !previousPayment) {
            return true
        }

        return previousPayment?.getId() !== newlyCreatedPayment?.getId()
    }

    const handleErrorEvent = () => {
        setPaymentFailed(true)
    }

    const handleClickEvent = () => {
        if (isPayingOrderForm()) {
            return
        }
        //can't poll when paying multiple invoices with one payment
        if (props.proposalHash) {
            pollForPaymentCreation()
        }
    }

    const isPayinConfig = (payinConfig: PayinConfig | HttpError | undefined)
        : payinConfig is PayinConfig => {
        return payinConfig != undefined && !isError(payinConfig)
    }

    const handleButtonClick = () => {
        setPaymentFailed(false)
        refetch()
    }

    if (!isAmountValid()) {
        return <div>
            <p className="text-danger">
                <FormattedMessage
                    id="common.bank_account.rainforest.error_msg"
                    defaultMessage="Please enter a valid payment amount. The amount should be a positive number greater than zero."
                />
            </p>
        </div>
    }

    const displayPaymentProcessor = isPayinConfig(payinConfigData)
    if (!displayPaymentProcessor || isLoadingData || isLoadingSession || isFetching
        || isCreatingTransaction || isTransactionSuccess) {
        return <><LoadingSpinner /></>
    }

    if (paymentFailed) {
        return (
            <div>
                <strong className="mr-auto">
                    <FormattedMessage
                        id="common.bank_account.rainforest.error_header"
                        defaultMessage="Error"
                    /></strong>
                <p className="text-danger">
                    <FormattedMessage
                        id="common.bank_account.rainforest.error_message"
                        defaultMessage="Uh oh. We couldn't process that payment. Make sure everything is correct, and try again."
                    />
                </p>
                <Button onClick={handleButtonClick}>
                    <FormattedMessage
                        id="common.bank_account.rainforest.try_again_btn"
                        defaultMessage="Try Again"
                    />
                </Button>
            </div>
        )
    }

    if (!isSupportedCurrency()) {
        return (
            <div className="py-4 px-2 d-flex gap-2">
                <WarningIcon className="text-warning" size={32} />
                <FormattedMessage
                    id="common.credit_card.rainforest_credit_card_wrapper.unsupported_currency"
                    defaultMessage="To process payments via QuoteMachine Payments, the account and document currency must be set to USD."
                />
            </div>
        )
    }

    const handleApprovedEvent = (event, payload: object) => {
        {/* @ts-ignore */}
        const rfResponse = event?.detail[0]?.data
        LoggerService.LogRequest({
            logMessage: "Payment Is Approved Event - ACH Payment",
            logBody: {
                payinConfigId: rfResponse.payin_config_id,
                amount: props.amount,
                payinId: rfResponse.payin_id,
                payload: payload,
            }
        })
    }

    const generateLogPayload = (logMessage, requestPayload, response) => {
        const payinConfigId = isPayinConfig(payinConfigData) ? payinConfigData.getId() : ""
        const identifiers = response?.data?.map(doc => doc.attributes.identifier).join(", ")
        const documentIds = response?.data?.map(doc => doc.id).join(", ")
        const errorMessage = response?.error?.message

        return {
            logMessage: logMessage,
            logBody: {
                payinConfigId: payinConfigId,
                amount: props.amount,
                paymentMethod: PAYMENT_TYPE.BANK_TRANSFER,
                proposalHash: props.proposalHash,
                payload: requestPayload,
                identifiers: identifiers,
                documentIds: documentIds,
                errorMessage: errorMessage
            }
        }
    }

    const tentativePaymentCreation = async () => {

        //paying proposal
        if (metaData && isPayinConfig(payinConfigData) && isProposal()) {
            const attributeId = documentTypeAttribute(metaData.documentType)
            let payload = {
                "note": "Online payment",
                "paymentType": PAYMENT_TYPE.BANK_TRANSFER,
                "currency": "USD",
                "contactId": metaData.contactId,
                "source": metaData.source,
                transactions: [
                    {
                        "amount": metaData.amount,
                        [attributeId]: metaData.documentId
                    }
                ],
                "fingerprintId": metaData.fingerprintId,
                "externalId": payinConfigData.getId(),
                "bankAccount": {
                    "processPaymentWithNonSavedBankAccount": true,
                },
                "locationId": metaData.locationId,
            } as OrderFormPayload

            if (isPayingOrderForm()) {
                payload = metaData.orderFormPayload
                payload.paymentType = PAYMENT_TYPE.BANK_TRANSFER
                payload.externalId = payinConfigData.getId()
                payload.bankAccount = {
                    locationId: metaData.locationId,
                    processPaymentWithNonSavedBankAccount: true,
                }
            } else if (isPayingAccountStatementProposal()) {
                const transactions = metaData.accountStatementInvoices.map(inv => {
                    return {
                        invoiceId: inv.id,
                        amount: inv.balance,
                    }
                }).filter(trans => parseFloat(trans.amount) > 0)
                payload.transactions = transactions
            }

            LoggerService.LogRequest(generateLogPayload(
                "Payment transaction request before",
                payload,
                {},
            ))

            const res = await ProposalService.createPaymentTransaction(props.proposalHash, payload)

            LoggerService.LogRequest(generateLogPayload(
                "Payment transaction request after",
                payload,
                res,
            ))

            if ((res as ProposalDto).id) {
                const component = document.querySelector("rainforest-payment")
                if (component) {
                    component.addEventListener("approved", (event) => handleApprovedEvent(event, payload))

                    LoggerService.LogRequest(generateLogPayload(
                        "Rainforest ach component submitting - online proposal",
                        payload,
                        res,
                    ))

                    {/* @ts-ignore */ }
                    component.submit()
                    if (isPayingOrderForm()) {
                        setFinalProposalHash((res as ProposalDto).attributes.hash)
                    }
                }
            } else {
                if ((res as APIError).error?.message) {
                    const errorMessage = (res as APIError).error?.message
                    setTentativeError(errorMessage || "")
                } else {
                    setTentativeError(props.intl.formatMessage(messages.genericErrorMessage))
                }
            }
            //paying from back-office
        } else {
            if (metaData && isPayinConfig(payinConfigData)) {
                const attributeId = documentTypeAttribute(metaData.documentType)
                const payload = {
                    "note": metaData.notes,
                    "paymentType": PAYMENT_TYPE.BANK_TRANSFER,
                    "currency": "USD",
                    "contactId": metaData.contactId,
                    "source": metaData.source,
                    transactions: [
                        {
                            "amount": metaData.amount,
                            [attributeId]: metaData.documentId
                        }
                    ],
                    "fingerprintId": metaData.fingerprintId,
                    "externalId": payinConfigData.getId(),
                    "bankAccount": {
                        processPaymentWithNonSavedBankAccount: true,
                        locationId: metaData.locationId
                    }
                }
                if (metaData?.transactions) {
                    payload.transactions = metaData.transactions
                }

                LoggerService.LogRequest(generateLogPayload(
                    "Payment transaction request before",
                    payload,
                    {},
                ))

                AppContext.getApiClient().post("/payment-transactions", payload).then(res => {

                    LoggerService.LogRequest(generateLogPayload(
                        "Payment transaction request after",
                        payload,
                        res,
                    ))

                    if (res.data) {
                        //when paying multiple invoices from contact page
                        if (!props.proposalHash) {
                            {/* @ts-ignore */ }
                            const response = res
                            if (response.data.length > 0) {
                                const lastIndex = response.data.length - 1
                                setFinalProposalHash(response.data[lastIndex].attributes.proposalHash)
                            }
                        }
                        const component = document.querySelector("rainforest-payment")
                        if (component) {
                            component.addEventListener("approved", (event) => handleApprovedEvent(event, payload))

                            LoggerService.LogRequest(generateLogPayload(
                                "Rainforest ach component submitting - back-office",
                                payload,
                                res,
                            ))

                            {/* @ts-ignore */ }
                            component.submit()
                        }
                    } else {
                        if ((res as APIError).error?.message) {
                            const errorMessage = (res as APIError).error?.message
                            setTentativeError(errorMessage || "")
                        } else {
                            setTentativeError(props.intl.formatMessage(messages.genericErrorMessage))
                        }
                    }
                })
            }
        }
    }

    const refreshProposal = () => {
        if (isProposal()) {
            window.location.href = "/p/proposals/" + props.proposalHash
        } else {
            window.location.reload()
        }
    }

    const documentTypeAttribute = (documentType: string) => {
        switch (documentType) {
        case "invoice":
            return "invoiceId"
        case "quote":
            return "quoteId"
        case "order":
            return "orderId"
        default:
            return ""
        }
    }

    return (
        isPolling ?
            <ProcessingPaymentLoader currencyCode={metaData?.currency || CURRENCY.USD} />
            :
            <>
                <RainforestPayment
                    sessionKey={(session as Session).getSessionKey()}
                    payinConfigID={payinConfigData.getId()}
                    allowedMethods='ACH'
                    handleClickEvent={handleClickEvent}
                    handleErrorEvent={handleErrorEvent}
                    method={RF_METHOD_TYPES.PAY_CC}
                    amount={props.amount}
                    currency={props.buildMetaData?.().currency}
                    refresh={tentativePaymentCreation}
                    tentativeError={tentativeError}
                />
                {!props.forceSaveBankAccount &&
                    <div className="row mb-3">
                        <div className="col-12">
                            <div className="ml-3 custom-control custom-checkbox">
                                <input
                                    style={{ zIndex: 10 }}
                                    name="saveBankAccountOnFile"
                                    type="checkbox"
                                    id="saveBankAccountOnFile"
                                    className="custom-control-input"
                                    checked={saveBankAccountOnFile}
                                    onChange={() => setSaveBankAccountOnFile(!saveBankAccountOnFile)}
                                />
                                <label className="custom-control-label" htmlFor="test">
                                    <FormattedMessage
                                        id="common.credit_card.save_bank_account_on_file_checkbox"
                                        defaultMessage="Save your bank account for future payments"
                                    />
                                </label>
                            </div>
                        </div>
                    </div>
                }
                {tentativeError &&
                    <RainforestErrorRefreshButton
                        errorMessage={tentativeError}
                        onClick={refreshProposal}
                    />
                }
            </>
    )
}
export default injectIntl(RainforestBankAccountWrapper)



// WEBPACK FOOTER //
// ./src/common/bank-account/rainforest/RainforestBankAccountWrapper.tsx